import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import Page from '../../../components/PageWrapper';
import { useEffect, useMemo, useState } from 'react';
import {
  adminAssignLead,
  getAllAdminAccounts,
  getAllLeadsSummaries,
  updateLeadTrail
} from '../../../api/requests';
import GTable from '../../../components/elements/GTable';
import moment from 'moment';
import { Lead } from '../../../models/lead/Lead';
import Button from '../../../components/form-control/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ITablePaginationData, ITableRowAction } from '../../../components/elements/GTable/GTable';
import PopOnMe from '../../../components/elements/poponme';
import useModal from '../../../hooks/modalHook';
import AssignLead from './AssignLead';
import {
  capitalizeSentence,
  getFormattedAccessRight,
  getFormattedDate,
  getFormattedLeadSource,
  getPhoneNumberFromShortCodeFormat,
  getVehicleProtectionFromKey,
  onRemoveFilter,
  sentenceCase,
  showToast
} from '../../../utils/miscFunctions';
import useRoute from '../../../hooks/routesHook';
import useUserAccount from '../../../hooks/userAccountHook';
import DateRangeSelectorModal from '../../../components/DateRangeSelectorModal';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ISelectDropdownItem } from '../../../components/form-control/SelectDropdown';
import { LEAD_SOURCE, LEAD_STATUS, LEAD_TAGS } from '../../../resources/enums';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import { IRequestResponse, ITableFilterItem } from '../../../resources/interfaces';
import { UserPlusIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import useLayoutHook from '../../../hooks/layoutHook';
import LeadTagStatus from './LeadTagStatus';
import EditLead from './EditLead';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { IUpdateLeadTrail } from './AddUpdate';
import SelectMenu from '../../../components/form-control/SelectMenu';
import TextArea from '../../../components/form-control/TextArea';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { Staff } from '../../../models/staff/Staff';
import { TableToggleAllRowsSelectedProps } from 'react-table';
import usePolicy from '../../../hooks/policyHook';
import { MdPolicy } from 'react-icons/md';
import { BadgePlus, Edit, UserCheck, UserCog, UserPlus } from 'lucide-react';
import useLeads from '../../../hooks/leadsHook';

interface ICell {
  cell: { value: string | null };
  row: {
    original: {
      firstname: string;
      lastname: string;
      id: string;
      owner: { firstName: string; lastName: string };
    };
  };
}
interface ICellOriginal {
  row: { original: Lead; getToggleRowSelectedProps: () => TableToggleAllRowsSelectedProps };
}

type HeaderProps = {
  getToggleAllRowsSelectedProps: () => TableToggleAllRowsSelectedProps;
};
interface ILeadsProps {
  showActions?: boolean;
  showTabs?: boolean;
}

const Leads = ({ showActions = true, showTabs = true }: ILeadsProps): JSX.Element => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const { user } = useUserAccount();
  const { screenWidth } = useLayoutHook();
  const { PolicyModalComponent } = usePolicy();
  const { getLeadSourceFilters } = useLeads()
  const [searchString, setSearchString] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [loading, setLoading] = useState<boolean>(true);
  const [leadsPaginationData, setLeadsPaginationData] = useState<ITablePaginationData>();
  const [showPerPage, setShowPerPage] = useState<number>(25);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const [admins, setAdmins] = useState<Staff[]>([]);
  const page = 'lead';
  const LeadTags: Array<string> = Object.values(LEAD_TAGS).map((value) => value);
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1;
  const [networkError, setNetworkError] = useState<boolean>(false);


  useEffect(() => {
    setTableFilters(getDefaultFilters());
  }, [admins]);

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'source',
        value: '',
        type: 'select',
        list: getLeadSourceFilters(),
        placeholder: `Select source`,
        label: `${capitalizeSentence('source')} `
      },
      {
        name: 'status',
        value: '',
        type: 'select',
        list: getLeadStatusFilters(),
        placeholder: `Select status`,
        label: `${capitalizeSentence('status')} `
      },
      {
        name: 'tag',
        value: '',
        type: 'select',
        list: getLeadTagsFilters(),
        placeholder: `Select tag`,
        label: `${capitalizeSentence('tag')} `
      },
      {
        name: 'owner',
        value: '',
        type: 'select',
        list: admins,
        placeholder: `Select owner`,
        label: `${capitalizeSentence('owner')} `
      },
      {
        name: 'created_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select date',
        label: 'Created Date Range'
      },
      {
        name: 'expiry_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select date',
        label: 'Insurance Expiry Date Range'
      }
    ];

    return filters;
  }

  function getLeadStatusFilters() {
    const sources: ISelectDropdownItem[] = [{ name: 'All', value: 'All' }];

    for (const key in LEAD_STATUS) {
      if (Object.prototype.hasOwnProperty.call(LEAD_STATUS, key)) {
        sources.push({
          name: key,
          value: key
        });
      }
    }

    return sources;
  }

  function getLeadTagsFilters() {
    const sources: ISelectDropdownItem[] = [{ name: 'All', value: 'All' }];

    for (const key in LEAD_TAGS) {
      if (Object.prototype.hasOwnProperty.call(LEAD_TAGS, key)) {
        sources.push({
          name: key,
          value: key
        });
      }
    }

    return sources;
  }

  const getAllAdmins = () => {
    getAllAdminAccounts()
      .then((response) => {
        const _admins = response?.map((admin: Staff) => ({
          name: `${admin.firstName} ${admin.lastName} (${getFormattedAccessRight(
            admin.accessRight
          )})`,
          value: admin.id
        }));
        setAdmins(_admins);
      })
      .catch((error: Error) => {
        console.log(error);
      });
  };

  const adminAssignLeadToSelf = (id: string) => {
    adminAssignLead(id)
      .then((response) => {
        showToast.success(response.message ?? 'Lead assigned successfully');
        getLeads();
      })
      .catch((error: IRequestResponse) => {
        showToast.error(error?.description ?? error.message);
      });
  };

  let status;
  let statusFromEnum = '';

  function selectedStatus(tag: string) {
    switch (tag) {
      case 'NEW':
        statusFromEnum = 'COLD';
        break;
      case 'CONTACTED':
        statusFromEnum = 'COLD';
        break;
      case 'QUALIFIED':
        statusFromEnum = 'COLD';
        break;
      case 'UNRESPONSIVE':
        statusFromEnum = 'COLD';
        break;
      case 'DISQUALIFIED':
        statusFromEnum = 'COLD';
        break;
      case 'IN_NEGOTIATION':
        statusFromEnum = 'WARM';
        break;
      case 'NURTURING':
        statusFromEnum = 'WARM';
        break;
      case 'CONVERTED':
        statusFromEnum = 'CLOSED';
        break;
      default:
        break;
    }
    status = statusFromEnum;
    return status;
  }

  const getTableRowActions = (data: Lead): ITableRowAction[] => {
    const editLead = (section: string) => {
      data &&
        updateModalStates({
          show: true,
          size: 'md',
          children: (
            <EditLead
              lead={data}
              onProceed={() => {
                closeModal();
                // queryClient.invalidateQueries({ queryKey: ['getLeads'], exact: true });
                getLeads();
              }}
              onClose={closeModal}
              section={section}
            />
          ),
          showCloseButton: false,
          classes: '!p-0 r-[5px]'
        });
    };

    const addUpdateToLead = () => {
      data &&
        updateModalStates({
          show: true,
          size: 'xl',
          children: (
            <>
              <Formik
                initialValues={{
                  description: '',
                  leadStatus: '',
                  tag: ''
                }}
                validateOnChange
                validationSchema={Yup.object().shape({
                  tag: Yup.string().required('Lead tag is required.'),
                  description: Yup.string().required('Description is required.')
                })}
                onSubmit={(values, { setSubmitting, setValues }) => {
                  const payload: IUpdateLeadTrail = {
                    ...values,
                    leadStatus: selectedStatus(values.tag)
                  };
                  setSubmitting(true);
                  updateLeadTrail(data.id, payload)
                    .then((response) => {
                      setValues(response.data);
                      showToast.success(response?.message ?? 'Update added successfully');
                    })
                    .catch((error: Error) => {
                      showToast.error(error.message ?? 'Failed to add update');
                    })
                    .finally(() => {
                      // setSubmit(false);
                      closeModal();
                      getLeads();
                    });
                }}>
                {({
                  values,
                  errors,
                  isSubmitting,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  touched,
                  setFieldValue
                }) => (
                  <form className="p-5" onSubmit={handleSubmit}>
                    <div className="">
                      <div className="">
                        <SelectMenu
                          label="Lead Tag"
                          name="tag"
                          listSelectedValue={values.tag}
                          list={LeadTags}
                          onChange={(val: string | null) => setFieldValue('tag', val)}
                          onBlur={handleBlur}
                          placeholder="Select lead tag"
                          required
                          wrapperClasses="w-full"
                          error={touched.tag ? errors.tag : undefined}
                          helperText={touched?.tag ? errors.tag : undefined}
                        />
                      </div>

                      <div className="mt-7">
                        <TextArea
                          name={'description'}
                          value={values.description}
                          onChange={handleChange}
                          placeholder="Write an update..."
                          rows={3}
                          required
                          error={touched.description ? errors.description : undefined}
                          helperText={touched?.description ? errors.description : undefined}
                        />
                      </div>
                    </div>
                    <div className="w-full flex justify-end mt-7">
                      <Button
                        label="Add update"
                        className="bg-primary-main w-fit  rounded-full"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      />
                    </div>
                  </form>
                )}
              </Formik>
            </>
          ),
          showCloseButton: false,
          classes: '!p-0 r-[5px]'
        });
    };

    const actions = [];
    {
      if (user.isOwner() || user.isSupervisor()) {
        actions.push(
          {
            label: `Assign ${capitalizeSentence(page)}`,
            icon: <UserPlus className="h-4 w-4" />,
            callback: () => {
              updateModalStates({
                show: true,
                size: 'sm',
                children: (
                  <AssignLead
                    onProceed={() => {
                      closeModal();
                      getLeads();
                    }}
                    onClose={closeModal}
                    leadData={data}
                  />
                ),

                classes: '!p-0 r-[5px]'
              });
            },
            data: data
          },
          {
            label: `Update Lead Tag`,
            icon: <BadgePlus className="h-4 w-4" />,
            callback: () => {
              editLead('INSURANCE_INFO_TAG_ONLY');
            },
            data: data
          }
        );
      }
    }
    {
      if (user.isAdministrator()) {
        actions.push(
          {
            label: `Edit ${capitalizeSentence(page)}`,
            icon: <Edit className="h-4 w-4" />,
            callback: (lead: Lead) => {
              editLead('INSURANCE_INFO');
            },
            data: data
          },
          {
            label: `Update Lead Tag`,
            icon: <BadgePlus className="h-4 w-4" />,
            callback: () => {
              editLead('INSURANCE_INFO_TAG_ONLY');
            },
            data: data
          },
          {
            label: `Add Update to Lead `,
            icon: <UserCog className="h-4 w-4" />,
            callback: () => {
              addUpdateToLead();
            },
            data: data
          }
        );
      }
    }
    {
      let lead: Lead = data;
      if (user.isAdministrator() && lead?.owner === null) {
        actions.push({
          label: `Assign ${page} to self`,
          icon: <UserCheck className="h-4 w-4" />,
          callback: (lead: Lead) => {
            let id = lead?.id;
            if (id) adminAssignLeadToSelf(id);
          },
          data: data
        });
      }
    }
    return actions;
  };

  const expireFrom = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'expiry_date')?.value?.from;
  const expireTo = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'expiry_date')?.value?.to;
  const createFrom = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'created_date')?.value?.from;
  const createTo = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'created_date')?.value?.from;
  const searchParam = searchString;
  const leadSource = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'source')?.value;
  const leadStatus = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'status')?.value;
  const staffID = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'owner')?.value;
  const tag = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'tag')?.value;

  const getLeads = async () => {
    try {
      const response = await getAllLeadsSummaries(
        currentPage,
        showPerPage,
        countryId,
        expireFrom,
        expireTo,
        createFrom,
        createTo,
        searchParam,
        leadSource,
        leadStatus === 'All' ? '' : leadStatus,
        staffID,
        tag
      );

      if (response) {
        const _leads = response?.data?.content;
        setLeads(_leads);

        const from = response?.data?.pageable?.offset + 1;
        const to =
          from + response?.data?.size > response?.data?.totalElements
            ? response?.data?.totalElements
            : from + response?.data?.size - 1;

        setLeadsPaginationData({
          ...leadsPaginationData,
          per_page: response?.data?.size ?? 0,
          total: response?.data?.totalElements ?? 0,
          totalPages: response?.data?.totalPages ?? 0,
          from,
          to
        });
      }
    } catch (error) {
      setNetworkError(true);
    } finally {
      setLoading(false);
    }
  };

  useQuery({
    queryKey: [
      'getLeads',
      currentPage,
      showPerPage,
      countryId,
      expireFrom,
      expireTo,
      createFrom,
      createTo,
      searchParam,
      leadSource,
      leadStatus,
      staffID,
      tag,
    ],
    queryFn: () => getLeads()
  });

  useQuery({
    queryKey: ['getAllAdmins'],
    queryFn: getAllAdmins
  });

  const mobileColumns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: HeaderProps) => (
          <div>
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: ICellOriginal) => (
          <div>
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstname',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell?.value ? (
                    <>
                      {row.row?.original?.firstname.charAt(0).toUpperCase() +
                        row?.row.original.firstname.slice(1)}{' '}
                      {row.row.original.lastname.charAt(0).toUpperCase() +
                        row.row.original.lastname.slice(1)}
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },

          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'leadStatus',
            Cell: (row: ICell) => {
              return (
                <PolicyStatus
                  status={capitalizeSentence(row.cell.value ?? LEAD_STATUS.COLD)}
                  classes="!w-[80px]"
                />
              );
            }
          },
          {
            Header: 'Owner',
            accessor: 'owner.firstName',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? (
                    <>
                      {row.row.original.owner.firstName.charAt(0).toUpperCase() +
                        row.row.original.owner.firstName.slice(1)}{' '}
                      {row.row.original.owner.lastName.charAt(0).toUpperCase() +
                        row.row.original.owner.lastName.slice(1)}
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: ICellOriginal) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const signupMobileColumns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: HeaderProps) => (
          <div>
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: ICellOriginal) => (
          <div>
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstname',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? (
                    <>
                      {row.row.original.firstname.charAt(0).toUpperCase() +
                        row.row.original.firstname.slice(1)}{' '}
                      {row.row.original.lastname.charAt(0).toUpperCase() +
                        row.row.original.lastname.slice(1)}
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },

          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: () => {
              return <span>{<PolicyStatus status={'Inactive'} classes="!w-[80px]" />}</span>;
            }
          }
        ]
      }
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'fullName',
            Cell: (row: ICell) => {
              return (
                <Link
                  to={`${getDashboardBase()}/leads/${row.row.original.id}`}
                  className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink"
                  target="_blank">
                  <span>{row.cell.value ? <>{capitalizeSentence(row.cell.value)}</> : '-'}</span>
                </Link>
              );
            }
          },

          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'leadStatus',
            Cell: (row: ICell) => {
              return (
                <PolicyStatus
                  status={capitalizeSentence(row.cell.value ?? LEAD_STATUS.COLD)}
                  classes="px-2 max-w-[90px]"
                />
              );
            }
          },
          {
            Header: 'Tag',
            accessor: 'tag',
            Cell: (row: ICell) => {
              return row.cell.value ? (
                <LeadTagStatus status={capitalizeSentence(row.cell.value)} classes="px-3" />
              ) : (
                <span>-</span>
              );
            }
          },
          {
            Header: 'Source',
            accessor: 'leadSource',
            Cell: (row: ICell) => {
              return <span>{row.cell.value ? getFormattedLeadSource(row.cell.value) : '-'}</span>;
            }
          },
          {
            Header: 'Current Insurer',
            accessor: 'currentInsurer',
            Cell: (row: ICell) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Insurance Type',
            accessor: 'insuranceType',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getVehicleProtectionFromKey(row.cell.value)?.title : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Expiry Date',
            accessor: 'expiryDate',
            Cell: (row: ICell) => {
              return (
                <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
              );
            }
          },
          {
            Header: 'Owner',
            accessor: 'ownerName',
            Cell: (row: ICell) => {
              return <span>{row.cell.value || '-'}</span>;
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: ICell) => {
              return (
                <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
              );
            }
          },
          {
            id: 'Action',
            Cell: (row: ICellOriginal) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  <PopOnMe actions={getTableRowActions(row.row.original)}>
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </PopOnMe>
                </div>
              );
            }
          }
        ]
      }
    ],
    ['getLeads']
  );

  const signupColumns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: HeaderProps) => (
          <div>
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: ICellOriginal) => (
          <div>
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'Name',
            accessor: 'firstname',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? (
                    <>
                      {capitalizeSentence(
                        row.row.original.firstname + ' ' + row.row.original.lastname
                      )}
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              );
            }
          },

          {
            Header: 'Phone',
            accessor: 'phone',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'leadStatus',
            Cell: (row: ICell) => {
              return (
                <PolicyStatus
                  status={capitalizeSentence(row.cell.value ?? LEAD_STATUS.COLD)}
                  classes="!w-[80px]"
                />
              );
            }
          },
          {
            Header: 'Current Insurer',
            accessor: 'currentInsurer',
            Cell: (row: ICell) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Insurance Type',
            accessor: 'insuranceType',
            Cell: (row: ICell) => {
              return (
                <span>
                  {row.cell.value ? getVehicleProtectionFromKey(row.cell.value)?.title : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Expiry Date',
            accessor: 'expiryDate',
            Cell: (row: ICell) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: ICell) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const showFilterByDateModal = () => {
    updateModalStates({
      show: true,
      size: 'lg',
      children: (
        <DateRangeSelectorModal
          onCheck={() => {
            // setSelectedDateRange(data);
            closeModal();
          }}
          onClose={() => {
            closeModal();
          }}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
  };

  const getTableColumns = () => {
    if (screenWidth >= 550) {
      return user.isRideSharing() ? signupColumns : columns;
    } else {
      return user.isRideSharing() ? signupMobileColumns : mobileColumns;
    }
  };

  return (
    <Page title={`${capitalizeSentence(page)}`}>
      <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md dark:shadow-darkBg-shadow">
        <div className="flex items-center justify-between">
          <div className="">
            <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h2 className="font-semibold text-3xl">{capitalizeSentence(page)}s</h2>
            </div>

            <p className="text-sm text-slate-400">
              View a list of all {page}s and their details.
            </p>
          </div>

          {!user?.isRideSharing() && (
            <div className="sm:flex justify-end pt-2 gap-4 md:pt-0">
              <Button
                bgColor="primary"
                label={`${capitalizeSentence('New Quote')}`}
                onClick={() => navigate(`${getDashboardBase()}/quotes/calculate`)}
                icon={<MdPolicy className="size-5 mr-1 rounded-full" />}
              />

              <div className=" flex justify-end items-center">
                <div className=" flex gap-4">
                  <Link to={`${getDashboardBase()}/leads/create-leads`}>
                    <Button
                      bgColor="primary"
                      label={`Add ${capitalizeSentence(page)}`}
                      icon={<UserPlusIcon className="size-5 mr-1" />}
                    />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto mt-4">
          <GTable
            showDateRangeSelector
            onDateChange={() => {
              showFilterByDateModal();
            }}
            columns={getTableColumns()}
            loading={loading}
            data={leads}
            classes="h-fit"
            onSearch={(searchString: string) => setSearchString(sentenceCase(searchString))}
            searchPlaceholder={'Search by name or phone number...'}
            tableType={'leads'}
            paginationData={leadsPaginationData}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            networkError={networkError}
            stickyHeader
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            onRowClick={(lead: Lead) => {
              window.open(`${getDashboardBase()}/leads/${lead?.leadId}`, '_self');
            }}
            selectedFilters={tableFilters}
            onRemoveFilter={(filter: ITableFilterItem, index: number) => {
              onRemoveFilter(filter, index, tableFilters, setTableFilters);
            }}
            showFilter={
              <TableFilter
                openFilter={openFilter}
                onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                selectedFilters={tableFilters}
                content={
                  <TableFilterForm
                    filters={tableFilters}
                    getValues={(filters: ITableFilterItem[]) => {
                      setTableFilters(filters);
                      setOpenFilter(false);
                    }}
                    onResetFilters={() => setTableFilters(getDefaultFilters())}
                  />
                }
              />
            }
          />
        </div>
        <PolicyModalComponent />
        <ModalComponent />
      </div>
    </Page>
  );
};

export default Leads;
